// "What was this for?" (shared/notes.ts): the field in the add, edit and New purchases sheets, its
// suggestion chips (this household's latest notes for the same merchant, loaded after the sheet
// opens; none, no chips) and the muted line under a purchase's merchant (no line when empty).
import { NOTE_LABEL, NOTE_MAX, NOTE_PARTNER_SEES, cleanNote } from '../../../shared/notes';
import { esc } from '../format';

/**
 * Where a split can be sent with this note: the partner's name and whether the split is on, as a
 * fixed state (`on`) or the id of its switch (`switchId`, followed live by main.ts syncNoteSees()).
 */
export type NoteSees = { partner: string; on: boolean; switchId?: undefined } | { partner: string; switchId: string; on?: undefined };

/** The line shows only while the split is on and the note is not empty (no note, nothing goes). */
export const seesShown = (splitOn: boolean, note: string | null | undefined) => splitOn && !!cleanNote(note);

/**
 * "Tiffany will see this.": under the field while a split is on and the note is not empty (the
 * split sends this note), hidden otherwise. main.ts syncNoteSees() keeps it in step as it is typed.
 */
export function noteSeesLine(id: string, value: string | null, sees: NoteSees, switchOn = !!sees.on): string {
  const show = seesShown(switchOn, value);
  const state = sees.switchId ? ` data-switch="${esc(sees.switchId)}"` : ` data-on="${sees.on ? '1' : '0'}"`;
  return `<p class="hint notesees" data-note-sees="${esc(id)}"${state}${show ? '' : ' hidden'}>${esc(NOTE_PARTNER_SEES(sees.partner))}</p>`;
}

/**
 * The field, with an empty slot for its chips under it. `attrs` adds data attributes. With `sees`
 * (a split can go with it), the "Tiffany will see this." line sits right under the field.
 */
export const noteField = (id: string, value: string | null, attrs = '', sees?: NoteSees) =>
  `<label class="fld"><span>${NOTE_LABEL}</span><input id="${id}" type="text" maxlength="${NOTE_MAX}" autocomplete="off" enterkeyhint="done" value="${esc(value ?? '')}"${attrs}></label>${sees ? noteSeesLine(id, value, sees) : ''}<div class="notechips" data-note-chips="${id}"></div>`;

/** Up to 4 chips, one line each (a long note ends in an ellipsis); tapping one fills the field. '' when there are none (the slot stays empty, no gap). */
export const noteChips = (fieldId: string, notes: readonly string[]) =>
  notes.map((n) => `<button class="chip notechip" data-a="note-chip" data-for="${esc(fieldId)}" data-note="${esc(n)}">${esc(n)}</button>`).join('');

/** The muted line under the merchant, or '' (no line at all) without a note. */
export const noteLine = (note: string | null | undefined) => {
  const n = cleanNote(note);
  return n ? `<span class="txnote">${esc(n)}</span>` : '';
};

/** Activity search text of a row (merchant as shown, bank text, note), lowercased. */
export const searchText = (t: { merchant: string; bankText?: string; note?: string | null }) =>
  [t.merchant, t.bankText ?? '', t.note ?? ''].join('\n').toLowerCase();
